import { flexRender } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  TableBody,
  TableHead,
  TableHeader,
  Table,
  TableRow,
} from "~/components/table";
import { useProjects } from "~/pages/projects/contexts/projects-context";
import { useTable } from "~/hooks/use-table";
import { ProjectsTableBody } from "~/components/projects-table/projects-table-body";
import { Button } from "~/components/button";
import { IProject } from "~/integrations/firebase/collections";
import { useAnalytics } from "~/contexts/analytics-context";
import { Text } from "~/components/text";
import { columns as getColumns } from "./columns";

export const RecentProjects = () => {
  const { projects, isLoadingRead, errorRead, count } = useProjects();
  const { setShowOnlyMyProjects } = useProjects();
  const { gaEvent } = useAnalytics();
  const navigate = useNavigate();

  const data = useMemo(() => projects.slice(0, 3), [projects]);

  useEffect(() => {
    setShowOnlyMyProjects(true);
  }, [setShowOnlyMyProjects]);

  const { t } = useTranslation(["components"]);

  const columns = useMemo(() => getColumns(t), [t]);

  const { table } = useTable({
    columns,
    data,
    totalRecords: count || 0,
    getRowId: (row) => row.id,
  });

  const handleRowClicked = useCallback(
    (row: IProject) => {
      navigate({ to: "/projects/$projectId", params: { projectId: row.id } });
      gaEvent({
        type: "view_item_list",
        payload: {
          item_list_name: row.name,
          item_list_id: row.id,
          item_category: "project",
        },
      });
    },
    [navigate, gaEvent],
  );

  return (
    <div className="space-y-4 text-left">
      <div className="flex flex-row justify-between items-center w-full">
        <Text as="h2" variant="heading">
          {t("home:recentProjects.title")}
        </Text>

        <Link to="/projects">
          <Button
            variant="link"
            className="text-primary -my-4 hover:text-primary-800"
            iconPosition="right"
            icon="arrow-right"
            label={t("home:recentProjects.allProjects")}
          />
        </Link>
      </div>

      <Table variant="shadowed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          <ProjectsTableBody
            isLoadingRead={isLoadingRead}
            errorRead={errorRead}
            projects={projects}
            table={table}
            columns={columns}
            onRowClicked={handleRowClicked}
          />
        </TableBody>
      </Table>
    </div>
  );
};
